import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Retourner des valeurs par défaut au lieu de lancer une erreur
    return {
      notifications: [],
      unreadCount: 0,
      markAsRead: () => {},
      markAllAsRead: () => {},
      clearAll: () => {}
    };
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef(null);

  // Fonction pour recalculer le compteur non lus à partir de la liste
  const recomputeUnreadCount = useCallback((items) => {
    const count = items.filter((n) => !n.read).length;
    setUnreadCount(count);
  }, []);

  // Fonction pour ajouter une notification (temps réel)
  const addNotification = useCallback((notification) => {
    setNotifications((prev) => {
      // éviter les doublons si l'id vient de la base
      if (notification.id && prev.some((n) => n.id === notification.id)) {
        return prev;
      }

      const next = [
        {
          ...notification,
          read: notification.read ?? false,
          timestamp: notification.timestamp || new Date().toISOString(),
        },
        ...prev,
      ];

      recomputeUnreadCount(next);
      return next;
    });

    // Notification audio ou visuelle (optionnel)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('ImmoCI - Nouvelle notification', {
        body: notification.message,
        icon: '/logo.png'
      });
    }
  }, []);

  // Marquer une notification comme lue
  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) => {
      const next = prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      );
      return next;
    });
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Marquer toutes les notifications comme lues (backend + state)
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
    } catch (error) {
      console.error('❌ Erreur lors du marquage de toutes les notifications comme lues:', error);
    }

    setNotifications((prev) => {
      const next = prev.map((notif) => ({
        ...notif,
        read: true,
        readAt: notif.readAt || new Date().toISOString(),
      }));
      return next;
    });
    setUnreadCount(0);
  }, []);

  // Effacer toutes les notifications (backend + state)
  const clearAll = useCallback(async () => {
    try {
      await axios.delete(`${API}/notifications`);
    } catch (error) {
      console.error('❌ Erreur lors de la suppression de toutes les notifications:', error);
    }

    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Chargement initial de l'historique de notifications
  useEffect(() => {
    if (!user) return;

    const loadNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications`);
        const items = response.data?.items || [];
        setNotifications(items);
        const count =
          typeof response.data?.unreadCount === 'number'
            ? response.data.unreadCount
            : items.filter((n) => !n.read).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des notifications:', error);
      }
    };

    loadNotifications();
  }, [user]);

  // Connexion SSE
  useEffect(() => {
    if (!user || !user.role) {
      return;
    }

    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    // Normalize role for API (CLIENT -> client, ADMIN -> admin, PROFESSIONAL -> professional)
    const normalizedRole = user.role.toLowerCase();
    const eventSourceUrl = `${apiUrl}/api/notifications/stream/${normalizedRole}`;

    console.log(`📡 Connexion SSE vers : ${eventSourceUrl}`);

    const eventSource = new EventSource(eventSourceUrl);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('✅ Connexion SSE établie');
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📬 Notification reçue:', data);

        if (data.type !== 'connected') {
          addNotification(data);
        }
      } catch (error) {
        console.error('❌ Erreur parsing notification:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('❌ Erreur SSE:', error);
      eventSource.close();
      
      // Tentative de reconnexion après 5 secondes
      setTimeout(() => {
        console.log('🔄 Tentative de reconnexion SSE...');
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      }, 5000);
    };

    // Demander la permission pour les notifications browser
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Cleanup
    return () => {
      if (eventSourceRef.current) {
        console.log('🔌 Fermeture connexion SSE');
        eventSourceRef.current.close();
      }
    };
  }, [user, addNotification]);

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
